<template>
  <div>
    <div class="px-10 pt-10">
      <span class="text-h5 font-weight-bold">
        {{ $t('SIDEBAR.ITEM.MY_SPACE') }}
      </span>

      <Async :loading="loading">
        <v-tabs
          centered
          hide-slider
          background-color="transparent"
          color=""
          v-model="currentTab"
        >
          <v-tab>
            <span
              class="tab_item"
              :class="{ active: currentTab === currentTabStatus.ALIVE }"
            >
              {{ $t('COMMON.WORD.ROOM') }}
              <SizeBox width="11" />
              <div class="count_block b-rad-8 flex-center">
                {{ info.aliveAmount }}
              </div>
            </span>
          </v-tab>
          <v-tab>
            <span
              class="tab_item"
              :class="{ active: currentTab === currentTabStatus.DELETED }"
            >
              {{ $t('SPACE.WORD.RECYCLED') }}
              <SizeBox width="11" />
              <div class="count_block b-rad-8 flex-center">
                {{ info.softDeletedAmount }}
              </div>
            </span>
          </v-tab>
        </v-tabs>

        <div class="flex-center">
          {{ $t('SPACE.COPYWRITING.CURRENTLY_USING') }}
          <span class="primary200--text mx-3">
            {{ info.usingAmount }} / {{ info.limitAmount }}
          </span>
          {{ $t('SPACE.COPYWRITING.ROOM_RECYCLED_INCLUDE') }}
        </div>

        <SizeBox height="29" />

        <v-slide-y-transition group tag="div" class="row" leave-absolute>
          <v-col
            v-if="currentTab === currentTabStatus.ALIVE"
            cols="4"
            key="addExpo"
          >
            <v-card
              class="new_expo fill-height"
              outlined
              :class="{ no_expo: showingExpos.length == 0 }"
            >
              <div class="content flex-center flex-column">
                <span class="text-body-2">
                  {{ $t('SPACE.COPYWRITING.CREATE_SPACE_FOR_YOUR_ITEMS') }}
                </span>
                <SizeBox height="24" />
                <v-tooltip bottom :disabled="!isReachLimit">
                  <template #activator="{ on }">
                    <v-btn
                      v-on="on"
                      height="49"
                      class="text-16px"
                      :ripple="!isReachLimit"
                      :color="isReachLimit ? 'neutral300' : 'primary'"
                      :to="
                        isReachLimit ? null : { name: 'exhibitions-create-new' }
                      "
                      @click="isReachLimit && popUpgradeDialog()"
                    >
                      <v-icon>mdi-plus</v-icon>
                      {{ $t('SPACE.WORD.CREATE_SPACE') }}
                    </v-btn>
                  </template>
                  <span>{{ $t('SPACE.COPYWRITING.SPACE_LIMIT_REACHED') }}</span>
                </v-tooltip>
              </div>
            </v-card>
          </v-col>
          <v-col cols="4" v-for="(expo, index) in showingExpos" :key="expo.id">
            <ExpoCard
              :expo-data="expo"
              :current-tab="currentTab"
              :reach-limit="isReachLimit"
              ref="ExpoCard"
              @onRevive="openRevive"
              @onDelete="openDelete"
              @onRemove="openRemove"
              @onToggle="beforeChangeStatus(expo, index)"
              @refresh="fetchData"
            />
          </v-col>
        </v-slide-y-transition>

        <div
          v-if="currentTab === currentTabStatus.DELETED && !deletedExpos.length"
          class="flex-center flex-column"
        >
          <SizeBox height="88" />
          <v-img
            :src="require(`@/assets/images/treasure_box_open.svg`)"
          ></v-img>
          <SizeBox height="32" />
          <p class="text-h5">
            {{ $t('SPACE.COPYWRITING.YOUR_RECYCLE_IS_EMPTY') }}
          </p>
        </div>
      </Async>
    </div>

    <SizeBox height="16" />
  </div>
</template>

<script>
import {
  getExhibitions,
  editExhibition,
  publishExhibition
} from '@/api/panel/exhibitions';
import ExpoCard from '@/components/exhibitions/ExpoCard.vue';

// enum
const currentTabStatus = {
  ALIVE: 0,
  DELETED: 1
};

export default {
  components: { ExpoCard },
  data() {
    return {
      loading: false,
      operatingExpoId: null,
      currentTabStatus,
      currentTab: 0,
      info: {
        aliveAmount: 0,
        softDeletedAmount: 0,
        limitAmount: 0,
        usingAmount: 0
      },
      deletedExpos: [],
      aliveExpos: []
    };
  },

  computed: {
    isReachLimit() {
      return this.info.usingAmount >= this.info.limitAmount;
    },
    showingExpos() {
      switch (this.currentTab) {
        case currentTabStatus.ALIVE:
          return this.aliveExpos;
        case currentTabStatus.DELETED:
          return this.deletedExpos;
        default:
          return this.aliveExpos;
      }
    }
  },

  watch: {
    currentTab(v) {
      if (v === currentTabStatus.ALIVE) {
        this.fetchAlive();
      }
      if (v === currentTabStatus.DELETED) {
        this.fetchDeleted();
      }
    }
  },

  created() {
    this.fetchData();
  },

  methods: {
    async fetchData() {
      this.loading = true;

      await this.fetchAlive();
      await this.fetchDeleted();

      this.loading = false;
    },
    async fetchAlive() {
      const { data, info, error } = await getExhibitions({
        deletedStatus: 'alive'
      });

      if (!error) {
        this.$set(this, 'aliveExpos', data.reverse());
        this.$set(this, 'info', info);
      }
    },
    async fetchDeleted() {
      const { data, info, error } = await getExhibitions({
        deletedStatus: 'softDeleted'
      });

      if (!error) {
        this.$set(this, 'deletedExpos', data.reverse());
        this.$set(this, 'info', info);
      }
    },
    popUpgradeDialog() {
      this.$store.dispatch('modal/ACTIVE_MODAL', {
        title: this.$t('SPACE.COPYWRITING.SPACE_LIMIT_REACHED'),
        content: this.$t('SPACE.COPYWRITING.PLEASE_UPGRADE_YOUR_PLAN'),
        action: {
          text: this.$t('SPACE.EDIT.WORD.UPGRADE_NOW'),
          bind: {
            to: { name: 'update-account' }
          },
          callback: () => this.$store.dispatch('modal/CLOSE_MODAL')
        },
        cancel: {
          text: this.$t('COMMON.WORD.CANCEL')
        }
      });
    },

    beforeChangeStatus(expo, index) {
      if (expo.status === 'unpublished') {
        this.$store.dispatch('modal/ACTIVE_MODAL', {
          title: this.$t('SPACE.POPUP.COPYWRITING.CONFIRM_PUBLISH'),
          content:
            this.$t('SPACE.POPUP.COPYWRITING.AFTER_PUBLISHED') +
            '\n' +
            this.$t('SPACE.POPUP.COPYWRITING.ANYONE_CAN_VISIT'),
          action: {
            text: this.$t('COMMON.WORD.PUBLISH'),
            callback: async () => {
              await this.toggleStatus(expo);
              this.$store.dispatch('modal/CLOSE_MODAL');
            }
          },
          cancel: {
            callback: () => {
              this.$refs.ExpoCard[index].setExhibitionStatus();
            },
            text: this.$t('COMMON.WORD.CANCEL')
          },
          close: {
            callback: () => {
              this.$refs.ExpoCard[index].setExhibitionStatus();
            }
          }
        });
      } else {
        this.toggleStatus(expo);
      }
    },

    async toggleStatus(expo) {
      let status = '';
      if (expo.status === 'published') status = 'unpublished';
      if (expo.status === 'unpublished') status = 'published';
      const Expo = await publishExhibition({
        id: expo.id,
        status
      });

      if (!Expo.error) {
        expo.status = Expo.status;
        this.$toast(
          `${this.$t('COMMON.WORD.EXHIBITION')}${this.$t(
            'COMMON.WORD.ALREADY'
          )} ${
            status === 'published'
              ? this.$t('COMMON.WORD.PUBLISH')
              : this.$t('COMMON.WORD.UNPUBLISH')
          }`
        );
      } else {
        !!Expo.error.message && this.$toast.error(Expo.error.message);
      }
    },

    openRemove(v) {
      this.operatingExpoId = v.id;
      this.$store.dispatch('modal/ACTIVE_MODAL', {
        title: this.$t('SPACE.POPUP.COPYWRITING.DELETE_SPACE'),
        content: this.$t(
          'SPACE.POPUP.COPYWRITING.DELETE_YOUR_SPACE_FIND_DELETED_SPACE'
        ),
        cancel: {
          bind: {
            outlined: false
          },
          text: this.$t('COMMON.WORD.CANCEL')
        },
        action: {
          text: this.$t('COMMON.WORD.DELETE'),
          color: 'primary',
          callback: this.removeExpo,
          bind: {
            outlined: true
          }
        }
      });
    },

    openRevive(v) {
      this.operatingExpoId = v.id;
      this.$store.dispatch('modal/ACTIVE_MODAL', {
        title: this.$t('SPACE.WORD.SPACE_RESTORED'),
        content:
          this.$t('SPACE.COPYWRITING.WILL_RESTORED_SPACE') +
          '\n' +
          this.$t('SPACE.COPYWRITING.WILL_SET_UNPUBLISHED'),
        action: {
          text: this.$t('COMMON.WORD.RESTORE'),
          color: 'primary',
          callback: this.reviveExpo
        },
        cancel: {
          text: this.$t('COMMON.WORD.CANCEL')
        }
      });
    },
    openDelete(v) {
      this.operatingExpoId = v.id;
      this.$store.dispatch('modal/ACTIVE_MODAL', {
        title: this.$t('SPACE.WORD.PERMANENT_REMOVE'),
        content: this.$t(
          'SPACE.POPUP.COPYWRITING.SPACE_REMOVED_CANNOT_BE_RESTORE'
        ),
        cancel: {
          bind: {
            outlined: false
          },
          text: this.$t('COMMON.WORD.CANCEL')
        },
        action: {
          text: this.$t('COMMON.WORD.REMOVE'),
          color: 'primary',
          callback: this.deleteExpo,
          bind: {
            outlined: true
          }
        }
      });
    },

    async removeExpo() {
      const { error } = await editExhibition({
        id: this.operatingExpoId,
        deletedStatus: 'softDeleted'
      });

      if (!error) {
        this.fetchData();
        this.$toast(this.$t('ARTWORK.EDIT.TOAST.SUCCESS.DELETE_SUCCESSFUL'));
        this.$store.dispatch('modal/CLOSE_MODAL');
      } else {
        this.$toast.error(this.$t('ARTWORK.EDIT.TOAST.ALERT.DELETE_FAIL'));
      }
    },

    async reviveExpo() {
      const { error } = await editExhibition({
        id: this.operatingExpoId,
        deletedStatus: 'alive'
      });

      if (!error) {
        this.fetchData();
        this.$toast(this.$t('SPACE.TOAST.SUCCESS.RESTORE_SUCCESFUL'));
        this.$store.dispatch('modal/CLOSE_MODAL');
      } else {
        this.$toast.error(this.$t('SPACE.TOAST.SUCCESS.RESTORE_FAILED'));
      }
    },

    async deleteExpo() {
      const { error } = await editExhibition({
        id: this.operatingExpoId,
        deletedStatus: 'foreverDeleted'
      });

      if (!error) {
        this.fetchData();
        this.$toast(this.$t('SPACE.TOAST.SUCCESS.REMOVE_SUCCESSFUL'));
        this.$store.dispatch('modal/CLOSE_MODAL');
      } else {
        this.$toast.error(this.$t('SPACE.COPYWRITING.REMOVE'));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-tab {
    min-width: auto;
    padding: 0;
    margin-right: 30px;
    &::before {
      opacity: 0 !important;
    }
    .v-ripple__container {
      display: none;
    }
  }
}
.tab_item {
  display: flex;
  align-items: center;
  color: $neutral300;

  .count_block {
    width: 26px;
    height: 26px;
    background-color: $menu2;
    color: $neutral100;
  }

  &.active {
    color: $neutral100;
    .count_block {
      background-color: $primary;
    }
  }
}

.new_expo {
  border: 1px dashed #878787;
  position: relative;
  &.no_expo {
    padding-bottom: min(80%, 385px);
  }
  .content {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    padding: 20px;
  }
  @media screen and (max-width: 1200px) {
    a {
      height: 40px !important;
      font-size: 14px !important;
      padding: 0 10px !important;
    }
  }
}
</style>
